import React, { useState, useMemo } from 'react'
import './order-refund.scss'
import { ColumnsType } from 'antd/lib/table'
import { ICashierOrder } from '../order-list/order-table/type'
import { Row } from '@/components/layout/layout'
import LayoutInput from '../../../goods-manage/components/form/layout-input/layout-input'
import { Radio } from 'antd'
import { setObj, deepClone, INPUT_PRICE_LIMIT_REGEXP } from '@/utils'
import Big from 'big.js'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage, showLayer } from '@/utils/layer'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Table } from 'antd'
import Iconfont from '@/components/iconfont/iconfont'
import Button from '@/components/button/button'
import RemarkPopover from '../../../goods-manage/components/remark-popover/remark-popover'
import classnames from 'classnames'
import { useUpdateEffect } from 'ahooks'
import OrderRefundConfirm from './order-refund-confirm'

interface orderRefundProps {
    goodList: ICashierOrder.GoodsList[]
    payments: ICashierOrder.Payment[]
    orderId: number
    orderType: number
}

export interface refundGoodsList extends ICashierOrder.GoodsList {
    refund_amount: number
    refund_subtotal: number
    return_stock: number
}

const CashierOrderRefund: React.FC<orderRefundProps> = props => {
    const cls = useBemClass('cashier-order-refund')
    const [remark, setRemark] = useState<string>('')
    const [allVal, setAllVal] = useState<number>(1)

    const [refundGoods, setRefundGoods] = useState<refundGoodsList[]>(
        props.goodList.map(g => ({
            ...g,
            refund_amount: g.amount,
            refund_subtotal: g.actual_payment,
            return_stock: 1,
        })),
    )

    useUpdateEffect(() => {
        const isRefundStock = refundGoods.every(r => r.return_stock === 1)
        const isOnlyRefundMoney = refundGoods.every(r => r.return_stock === 0)

        setAllVal(isRefundStock ? 1 : isOnlyRefundMoney ? 0 : 2)
    }, [refundGoods])

    const columns: ColumnsType<refundGoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            ellipsis: true,
            dataIndex: 'goods',
            render: val => val.name,
        },
        {
            title: '售价',
            key: 'supplier',
            render: (v, r) => r.sale_price.sale_price,
        },
        {
            title: '数/重量',
            width: 70,
            dataIndex: 'amount',
            render: (val, r) => r.amount + r.settle_unit.name,
        },
        {
            title: '实付',
            key: 'actual_payment',
            dataIndex: 'actual_payment',
            align: 'right',
            render: v => `¥${v}`,
        },
        {
            title: '退款方式',
            width: 340,
            key: 'return_stock',
            dataIndex: 'return_stock',
            align: 'center',
            render: (val, record, index) => {
                return (
                    <Row>
                        <Radio.Group
                            onChange={e => {
                                setRefundGoods(
                                    deepClone(setObj(refundGoods, `[${index}].return_stock`, e.target.value)),
                                )
                            }}
                            value={val}
                        >
                            <Radio value={0}>仅退款</Radio>
                            <Radio value={1}>退款退库存</Radio>
                            <Radio value={2}>不退款</Radio>
                        </Radio.Group>
                    </Row>
                )
            },
        },
        {
            title: '退货量',
            key: 'refund_amount',
            width: 130,
            dataIndex: 'refund_amount',
            render: (val, record, index) => (
                <Row data-align-items-center>
                    <LayoutInput
                        value={val}
                        placeholder="请录入"
                        maxNum={record.amount}
                        onBlur={e => {
                            const amount = parseFloat(e) || 0

                            setRefundGoods(
                                deepClone(
                                    setObj(
                                        setObj(refundGoods, `[${index}].refund_amount`, amount),
                                        `[${index}].refund_subtotal`,
                                        +Big(record.actual_payment).div(record.amount).times(amount).round(3),
                                    ),
                                ),
                            )
                        }}
                    />
                    {record.settle_unit.name}
                </Row>
            ),
        },
        {
            title: '退货金额',
            key: 'refund_subtotal',
            width: 130,
            dataIndex: 'refund_subtotal',
            render: (val, record, index) => (
                <Row data-align-items-center>
                    <LayoutInput
                        value={val}
                        placeholder="请录入"
                        valueRegExp={INPUT_PRICE_LIMIT_REGEXP}
                        maxNum={record.actual_payment}
                        onBlur={e => {
                            const amount = parseFloat(e) || 0

                            setRefundGoods(
                                deepClone(
                                    setObj(
                                        setObj(refundGoods, `[${index}].refund_subtotal`, amount),
                                        `[${index}].refund_amount`,
                                        +Big(amount).div(record.actual_payment).times(record.amount).round(3),
                                    ),
                                ),
                            )
                        }}
                    />
                    元
                </Row>
            ),
        },
    ]

    // 总退款金额
    const totalRefundAmount = useMemo(() => {
        return refundGoods.reduce((pre, next) => {
            return next.return_stock === 2 ? pre : pre.plus(next.refund_subtotal)
        }, Big(0))
    }, [refundGoods])

    return (
        <BaseDialog isShow width={993} title="退款" onClose={() => layerManage.pop()}>
            <div className={cls('table-container')}>
                <Table
                    columns={columns}
                    rowKey={r => r.id}
                    dataSource={refundGoods}
                    pagination={false}
                    bordered
                    scroll={{ x: 'max-content', y: 225 }}
                ></Table>
                <div className={cls('tab')}>
                    <Row data-align-items-center data-color-333 data-font-size-14>
                        共
                        <span className="them-color" data-margin-h-4>
                            {refundGoods.length}
                        </span>
                        件
                    </Row>
                    <div
                        style={{
                            marginLeft: '80px',
                        }}
                    >
                        <Radio.Group
                            onChange={e => {
                                const val = e.target.value
                                setRefundGoods(good => {
                                    return good.map(g => ({
                                        ...g,
                                        return_stock: val,
                                    }))
                                })
                                setAllVal(e.target.value)
                            }}
                            value={allVal}
                        >
                            <Radio value={0}>全部仅退款</Radio>
                            <Radio value={1}>全部退款退库存</Radio>
                        </Radio.Group>
                    </div>
                    <Row data-align-items-center data-color-333 data-font-size-14>
                        退
                        <span className="danger-color" data-margin-left-4 data-font-weight-600>
                            ¥{+totalRefundAmount.round(2)}
                        </span>
                    </Row>
                </div>
                <div className={cls('bottom')}>
                    <RemarkPopover
                        value={remark}
                        maxLen={50}
                        onConfirm={e => {
                            setRemark(e)
                        }}
                    >
                        {remark ? (
                            <Row data-align-items-center>
                                <Row>
                                    <span data-color-333 data-font-size-16>
                                        备注：
                                    </span>
                                    <div className={classnames(cls('remark'), 'two-line')}>{remark}</div>
                                </Row>
                                <Iconfont name="icon6youjiantou-20" size={16} />
                            </Row>
                        ) : (
                            <Row data-align-items-center data-color-333 data-font-size-16 data-cursor-pointer>
                                <Iconfont name="icon6cunweicaogao-20" size={20} />
                                <span
                                    style={{
                                        color: '#3C90FF',
                                        marginLeft: '6px',
                                    }}
                                >
                                    添加备注
                                </span>
                            </Row>
                        )}
                    </RemarkPopover>
                    <Button
                        types="danger"
                        onClick={() => {
                            layerManage.pop()
                            showLayer(
                                <OrderRefundConfirm
                                    orderId={props.orderId}
                                    orderType={props.orderType}
                                    refundGoods={refundGoods}
                                    remark={remark}
                                    subtotal={+totalRefundAmount.round(2)}
                                    payments={props.payments}
                                />,
                            )
                        }}
                    >
                        确认
                    </Button>
                </div>
            </div>
        </BaseDialog>
    )
}

export default CashierOrderRefund
